import { useState } from 'react'
import type { ConversationListItemDTO, StoreDTO } from '@sac/contracts'
import { useUi, type StatusFilter } from '../../state/UiContext'
import { Icon } from '../icons'
import { Menu } from '../ui/Menu'
import { ConversationCard } from './ConversationCard'
import { NewConversation } from './NewConversation'

/**
 * Segundo lugar para "Não lidas", que é a pergunta que se faz ao abrir a caixa: o que chegou e
 * ninguém tratou. O filtro já existia no estado e era acionável de lugar nenhum.
 *
 * A fileira rola horizontalmente e recorta o que não cabe, então ordem é prioridade — e por isso
 * "Não lidas" fica antes das que dependem de alguém ter classificado a conversa.
 */
const STATUS_PILLS: [StatusFilter, string][] = [
  ['all', 'Tudo'],
  ['unread', 'Não lidas'],
  ['open', 'Em atendimento'],
  ['unassigned', 'Não atribuídas'],
  ['waiting', 'Aguardando'],
  ['resolved', 'Resolvidas'],
]

export function Inbox({
  items,
  allItems,
  stores,
  agents,
  onSelect,
}: {
  items: ConversationListItemDTO[]
  allItems: ConversationListItemDTO[]
  stores: StoreDTO[]
  agents: { id: string; name: string }[]
  onSelect: (id: string) => void
}) {
  const { state, dispatch } = useUi()
  const [nova, setNova] = useState(false)

  const naoLidas = allItems.filter((i) => i.unreadCount > 0).length
  const emAtendimento = allItems.filter((i) => i.status === 'open').length
  const naoAtribuidas = allItems.filter((i) => !i.assignedAgentId && i.status !== 'resolved').length

  const storeLabel =
    state.storeIds.size === 0
      ? 'Todas as lojas'
      : state.storeIds.size === 1
        ? stores.find((s) => state.storeIds.has(s.id))?.name
        : `${state.storeIds.size} lojas`

  const advOn = state.advAgent || state.advPriority || state.advSla

  return (
    <aside className={`inbox ${state.inboxOpen ? 'open' : ''}`}>
      <div className="inbox-top">
        {nova && (
          <NewConversation
            onClose={() => setNova(false)}
            onOpened={(id) => {
              // Limpa o filtro de status: uma conversa recém-aberta nasce "nova", e com
              // "Em atendimento" selecionado ela abriria e sumiria da lista no mesmo instante.
              dispatch({ type: 'setStatus', status: 'all' })
              onSelect(id)
            }}
          />
        )}
        <div className="inbox-top-row">
        <Menu
          trigger={(open) => (
            <button className="store-btn" onClick={open}>
              <Icon name="box" size={14} />
              <b>{storeLabel}</b>
              <Icon name="chev" size={12} />
            </button>
          )}
        >
          {() => (
            <>
              <button
                className={`pop-item ${state.storeIds.size === 0 ? 'on' : ''}`}
                onClick={() => dispatch({ type: 'clearStores' })}
              >
                Todas as lojas
                {state.storeIds.size === 0 && (
                  <span className="ck">
                    <Icon name="ck" />
                  </span>
                )}
              </button>
              <div className="pop-sep" />
              {stores.map((s) => (
                <button
                  key={s.id}
                  className={`pop-item ${state.storeIds.has(s.id) ? 'on' : ''}`}
                  onClick={() => dispatch({ type: 'toggleStore', storeId: s.id })}
                >
                  {s.name}
                  {state.storeIds.has(s.id) && (
                    <span className="ck">
                      <Icon name="ck" />
                    </span>
                  )}
                </button>
              ))}
            </>
          )}
        </Menu>
        <button className="new-conv-btn" onClick={() => setNova(true)} title="Nova conversa no WhatsApp pelo número">
          <Icon name="plus" size={14} />
          Nova
        </button>
        </div>

        <div className="search">
          <Icon name="search" />
          <input
            placeholder="Buscar cliente, pedido ou conversa…"
            value={state.search}
            onChange={(e) => dispatch({ type: 'setSearch', search: e.target.value })}
          />
          {state.search && (
            <button className="clr on" onClick={() => dispatch({ type: 'setSearch', search: '' })}>
              <Icon name="x" size={13} />
            </button>
          )}
        </div>

        <div className="filters">
          <div className="fpills">
            {STATUS_PILLS.map(([k, label]) => (
              <button
                key={k}
                className={`fpill ${state.status === k ? 'on' : ''}`}
                onClick={() => dispatch({ type: 'setStatus', status: k })}
              >
                {label}
              </button>
            ))}
          </div>
          <Menu
            align="right"
            trigger={(open) => (
              <button className={`fbtn ${advOn ? 'on' : ''}`} onClick={open} title="Filtros avançados">
                <Icon name="dots" />
              </button>
            )}
          >
            {(close) => (
              <div style={{ minWidth: 220 }}>
                <div className="pop-h">Agente</div>
                <div style={{ padding: '0 6px 6px' }}>
                  <select
                    style={{
                      width: '100%',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      padding: '6px 8px',
                      fontSize: 13,
                    }}
                    value={state.advAgent}
                    onChange={(e) => dispatch({ type: 'setAdv', agent: e.target.value })}
                  >
                    <option value="">Todos</option>
                    {agents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="pop-h">Prioridade</div>
                <div style={{ padding: '0 6px 6px' }}>
                  <select
                    style={{
                      width: '100%',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      padding: '6px 8px',
                      fontSize: 13,
                    }}
                    value={state.advPriority}
                    onChange={(e) => dispatch({ type: 'setAdv', priority: e.target.value })}
                  >
                    <option value="">Todas</option>
                    <option value="high">Alta</option>
                    <option value="normal">Normal</option>
                    <option value="low">Baixa</option>
                  </select>
                </div>
                <label className="pop-item">
                  <input
                    type="checkbox"
                    style={{ accentColor: 'var(--accent)' }}
                    checked={state.advSla}
                    onChange={(e) => dispatch({ type: 'setAdv', sla: e.target.checked })}
                  />
                  &nbsp;Apenas fora do SLA
                </label>
                <div className="pop-sep" />
                <button
                  className="btn ghost sm"
                  style={{ width: '100%' }}
                  onClick={() => {
                    dispatch({ type: 'setAdv', agent: '', priority: '', sla: false })
                    close()
                  }}
                >
                  Limpar
                </button>
              </div>
            )}
          </Menu>
        </div>
      </div>

      <div className="summary">
        <button onClick={() => dispatch({ type: 'setStatus', status: 'unread' })}>{naoLidas} não lidas</button>
        {' · '}
        <button onClick={() => dispatch({ type: 'setStatus', status: 'open' })}>{emAtendimento} em atendimento</button>
        {' · '}
        <button onClick={() => dispatch({ type: 'setStatus', status: 'unassigned' })}>{naoAtribuidas} não atribuídas</button>
      </div>

      <div className="list">
        {items.length === 0 ? (
          <div className="empty">
            Nenhuma conversa encontrada.
            <br />
            Ajuste a busca ou os filtros.
          </div>
        ) : (
          items.map((item) => (
            <ConversationCard
              key={item.id}
              item={item}
              active={state.currentConversationId === item.id}
              onClick={() => onSelect(item.id)}
            />
          ))
        )}
      </div>
    </aside>
  )
}
